import React from 'react';
import { Zap, ShoppingBag, Sparkles, RefreshCw, MapPin, Heart, Shield, Star } from 'lucide-react';

const ForConsumers = () => {
  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Get your orders in minutes, not days. Perfect for when you need it now.',
      stat: 'Minutes'
    },
    {
      icon: MapPin,
      title: 'Support Local',
      description: 'Shop from your neighborhood stores and support local businesses.',
      stat: 'Hyperlocal'
    },
    {
      icon: Sparkles,
      title: 'AI Recommendations',
      description: 'Smart suggestions based on your preferences and shopping history.',
      stat: 'Personalized'
    },
    {
      icon: ShoppingBag,
      title: 'Try & Buy',
      description: 'Experience products like in-store shopping with easy returns.',
      stat: 'Hassle-free'
    },
    {
      icon: RefreshCw,
      title: 'Seamless Refunds',
      description: 'Quick and easy returns with instant refund processing.',
      stat: 'Instant'
    },
    {
      icon: Heart,
      title: 'Curated Selection',
      description: 'Quality products from trusted local retailers you know and love.',
      stat: 'Trusted'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-cream/20 to-gray-50">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-block px-4 py-2 bg-coral-light rounded-full mb-6">
            <span className="text-sm font-semibold text-coral">For Consumers</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Shopping Made
            <span className="block text-coral mt-2">Simple & Fast</span>
          </h2>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            Experience the perfect blend of online convenience and in-store quality. 
            Get what you need, when you need it, from stores you trust.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="feature-card group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-coral flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-xl font-bold text-gray-900 flex-1">{feature.title}</h3>
                      <span className="px-3 py-1 bg-coral-light text-coral text-xs font-semibold rounded-full flex-shrink-0 whitespace-nowrap">
                        {feature.stat}
                      </span>
                    </div>
                    <p className="text-gray-600 leading-relaxed break-words">{feature.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Customer Journey */}
        <div className="bg-white rounded-3xl p-12 shadow-lg border border-gray-100">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Your Shopping Journey
          </h3>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-coral to-coral/70 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                1
              </div>
              <h4 className="font-bold text-gray-900 text-lg">Browse</h4>
              <p className="text-gray-600 text-sm">Explore products from local stores near you</p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-coral to-coral/70 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                2
              </div>
              <h4 className="font-bold text-gray-900 text-lg">Select</h4>
              <p className="text-gray-600 text-sm">Add items to cart with AI-powered suggestions</p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-coral to-coral/70 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                3
              </div>
              <h4 className="font-bold text-gray-900 text-lg">Order</h4>
              <p className="text-gray-600 text-sm">Place order with secure payment options</p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-coral to-coral/70 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                4
              </div>
              <h4 className="font-bold text-gray-900 text-lg">Receive</h4>
              <p className="text-gray-600 text-sm">Get delivery in minutes from local store</p>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <Shield className="w-12 h-12 mx-auto text-coral mb-3" />
            <p className="font-semibold text-gray-900">Secure Payments</p>
          </div>
          <div className="text-center">
            <RefreshCw className="w-12 h-12 mx-auto text-coral mb-3" />
            <p className="font-semibold text-gray-900">Easy Returns</p>
          </div>
          <div className="text-center">
            <Star className="w-12 h-12 mx-auto text-coral mb-3" />
            <p className="font-semibold text-gray-900">Quality Products</p>
          </div>
          <div className="text-center">
            <Heart className="w-12 h-12 mx-auto text-coral mb-3" />
            <p className="font-semibold text-gray-900">Local Support</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForConsumers;
